import { useCallback, useMemo, useState } from "react"

import hookCollectionStoreFactory from "@/stores/base-factories/store-collection";
import hookCollectionsStoreFactory from "@/stores/base-factories/store-collections";
import { hookCMSBuilderBlockList, hookSiteList } from "@/stores/base-factories/store-list";
import createStore from "@Stores/base-factories"
import hookSiteSelectedFactory from "@Stores/base-factories/store-site-selection";

import { parseDefinition } from "./utils";

const { Provider, useStore } = createStore()
  .extend(hookSiteSelectedFactory('siteId'))
  .extend(hookSiteList('sites'))
  .extend(hookCMSBuilderBlockList('templates'))
  .extend(hookCMSBuilderBlockList('layouts'))
  .extend(hookCMSBuilderBlockList('partials'))
  .extend(hookCollectionStoreFactory('cms-builder/block', { key: 'postBuilder' }))
  .extend(hookCollectionsStoreFactory('cms-builder/blocks', { key: 'postBuilders' }))
  .extend(hookCollectionStoreFactory('cms-builder/block', { key: 'block' }))
  .extend(hookCollectionsStoreFactory('cms-builder/blocks', { key: 'blocks' }))
  .extend(({ block: { fetchById }, siteSelected, fetch }) => {
    const [loading, setLoading] = useState(false)

    const fetchBlockById = useCallback(async id => {
      try {
        setLoading(true)
        const block = await fetchById(id)
        return block
      } finally {
        setLoading(false)
      }
    }, [fetchById])

    const fetchBlockVersion = useCallback(async id => {
      const block = await fetchById(id)
      return block?.meta?.version
    }, [fetchById])

    const fetchBlocksTags = useCallback(async () => {
      const { key: siteKey, id: siteId } = siteSelected
      return await fetch({
        url: `/cms-builder/blocks/tags`,
        params: {
          [siteKey]: siteId,
        },
      })
    }, [fetch, siteSelected])

    const uploadBlockImage = useCallback(async data => {
      const { key: siteKey, id: siteId } = siteSelected
      return await fetch({
        headers: { 'Content-Type': undefined },
        method: 'POST',
        url: '/cms-builder/block/image',
        data,
        params: {
          [siteKey]: siteId,
        },
      })
    }, [fetch, siteSelected])

    const saveBlock = useCallback(async payload => {
      const { key: siteKey, id: siteId } = siteSelected
      return await fetch({
        method: "PUT",
        url: `/cms-builder/block/${payload.id}/block`,
        data: payload,
        params: {
          [siteKey]: siteId,
        },
      })
    }, [fetch, siteSelected])

    return useMemo(() => ({
      templateBlock: {
        loading,
        fetchBlockVersion,
        fetchBlockById,
        fetchBlocksTags,
      },
      saveBlock,
      uploadBlockImage,
    }), [loading, fetchBlockVersion, fetchBlockById, fetchBlocksTags, saveBlock, uploadBlockImage])
  })
  .extend(({ postBuilder, postBuilder: { item } }) => useMemo(() => ({
    postBuilder: {
      ...postBuilder,
      item: item ? { ...item, craft: parseDefinition(item?.definition) } : null,
    },
  }), [postBuilder, item]))
  .factory()

export {
  Provider as BlockStoreProvider,
  useStore as useBlockStore,
}
